import { Form, Modal, Typography } from "antd"
import React, { useState } from "react"

import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import type { FinancialAlert } from "./FinancialAlertBanner.tsx"

const { Text } = Typography

interface UpdateContractValueModalProps {
  open: boolean
  alert: FinancialAlert
  /** The figure the overrun is measured against today. */
  currentValue: number
  /** The total that triggered the alert — the smallest figure that clears it. */
  suggestedValue: number
  onCancel: () => void
  onSubmit: (value: number) => Promise<void> | void
}

const isExpenseSide = (rule: string) => rule === "contract_expenses_exceeded"

/**
 * Opens the contract figure prefilled, never sets it.
 *
 * Decision 15 is explicit that the system never moves Sales Price on its own,
 * so this confirms a number rather than applying one — the field is editable
 * and the prefill is only the smallest value that would clear the alert.
 */
export function UpdateContractValueModal({
  open,
  alert,
  currentValue,
  suggestedValue,
  onCancel,
  onSubmit
}: UpdateContractValueModalProps) {
  const [form] = Form.useForm<{ value: number }>()
  const [saving, setSaving] = useState(false)

  const label = isExpenseSide(alert.rule)
    ? "Contract Expenses"
    : "Contract Value"

  const handleOk = async () => {
    if (saving) return
    const { value } = await form.validateFields()
    setSaving(true)
    try {
      await onSubmit(value)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={`Update ${label}`}
      open={open}
      okText="Save"
      cancelText="Cancel"
      onOk={handleOk}
      onCancel={onCancel}
      maskClosable={false}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnHidden
    >
      <p>
        <Text type="secondary">
          {label} is currently {budgetFormat(currentValue)}, and the
          project&apos;s{" "}
          {isExpenseSide(alert.rule) ? "payables" : "receivables"} come to{" "}
          {budgetFormat(suggestedValue)}.
        </Text>
      </p>

      <Form
        form={form}
        layout="vertical"
        initialValues={{ value: suggestedValue }}
      >
        <Form.Item
          name="value"
          label={`New ${label}`}
          rules={[
            { required: true, message: `Please enter the new ${label}` },
            {
              type: "number",
              transform: (v: unknown) =>
                isNaN(+(v as number)) ? 0 : +(v as number),
              min: 0,
              message: "Must be at least 0"
            }
          ]}
        >
          <InputNumber
            min={0}
            max={999999999.99}
            step={0.1}
            precision={2}
            className="w-full"
            prefix="$"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
